import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { StyleFormat, DownloadFormat, DocumentDownloadOptions } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface UseChronologyDownloadProps {
  chronologyId: string;
  matterId: string;
  subsetId: string;
}

interface ExportResponse {
  exportId: string;
  jobId: string;
}

const useChronologyDownload = ({ chronologyId, matterId, subsetId }: UseChronologyDownloadProps) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadChronology = async ({
    styleFormat,
    title,
    columns,
    style,
    downloadFormat,
    documentOptions,
    includeSourceDocuments = false,
    orderByDate,
  }: {
    styleFormat: StyleFormat;
    downloadFormat: DownloadFormat;
    title: string;
    columns?: string[];
    style?: string;
    documentOptions?: DocumentDownloadOptions;
    includeSourceDocuments?: boolean;
    orderByDate?: boolean;
  }): Promise<ExportResponse> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        downloadFormat,
        styleFormat,
        title,
        columns,
        style,
        documentOptions,
        orderByDate,
      },
    });

    const endpoint = includeSourceDocuments
      ? `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/download-with-documents`
      : `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/download`;

    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.message ?? 'Failed to start chronology export');
    }

    return await response.json();
  };

  return {
    downloadMutation: useMutation(downloadChronology, {
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to download chronology. Please try again.');
      },
    }),
  };
};

export default useChronologyDownload;
